import React, { useMemo } from "react";
import {
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Typography
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { StyledCard } from "./styled";

const COLUMN_DEFINITIONS = [
  {
    label: "Bar ID",
    width: "220px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}>
        {record.bar_id}
      </Typography>
    )
  },
  {
    label: "Name",
    width: "200px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
        {record.bar_name || "Unnamed"}
      </Typography>
    )
  },
  {
    label: "Address",
    width: "260px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
        {record.bar_address || "—"}
      </Typography>
    )
  },
  {
    label: "Created By",
    width: "160px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
        {record.created_by || "—"}
      </Typography>
    )
  },
  {
    label: "Owner Email",
    width: "200px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
        {record.owner_email || "—"}
      </Typography>
    )
  },
  {
    label: "Phone",
    width: "150px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
        {record.bar_phone || "—"}
      </Typography>
    )
  },
  {
    label: "Website",
    width: "220px",
    render: (record) => (
      record.bar_url ? (
        <Typography
          variant="body2"
          component="a"
          href={record.bar_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "0.75rem", color: "primary.main", wordBreak: "break-all" }}
        >
          {record.bar_url}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>—</Typography>
      )
    )
  },
  {
    label: "Active Promotions",
    width: "160px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
        {record.active_promotion_count || 0}
      </Typography>
    )
  },
  {
    label: "Has Promotion",
    width: "140px",
    render: (record) => (
      <Chip
        size="small"
        label={record.has_active_promotion ? "Yes" : "No"}
        color={record.has_active_promotion ? "success" : "default"}
        variant={record.has_active_promotion ? "filled" : "outlined"}
      />
    )
  },
  {
    label: "Next Expiry",
    width: "180px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        {formatTimestamp(record.next_promotion_expiry_utc)}
      </Typography>
    )
  },
  {
    label: "Crowd Count",
    width: "200px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
        {formatCrowdCount(record.crowd_count)}
      </Typography>
    )
  },
  {
    label: "Hours",
    width: "220px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
        {formatHours(record.hours)}
      </Typography>
    )
  },
  {
    label: "Geohash",
    width: "160px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
        {record.geohash || "—"}
      </Typography>
    )
  },
  {
    label: "Location",
    width: "220px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}>
        {formatLocation(record.location)}
      </Typography>
    )
  },
  {
    label: "Categories",
    width: "260px",
    render: (record) => renderCategories(record.categories)
  },
  {
    label: "Created At",
    width: "180px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        {formatTimestamp(record.created_at)}
      </Typography>
    )
  },
  {
    label: "Updated At",
    width: "180px",
    render: (record) => (
      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        {formatTimestamp(record.updated_at)}
      </Typography>
    )
  }
];

const BarRecordsPreview = ({
  barRecords,
  selectedRows,
  onSelectAll,
  onSelectRow,
  onRequestDelete,
  onRefresh,
  loading
}) => {
  const latestCreatedAt = useMemo(() => {
    if (!barRecords || barRecords.length === 0) {
      return "—";
    }

    const latestTimestamp = Math.max(
      ...barRecords.map((record) => {
        const value = record.created_at || record.updated_at;
        const date = parseTimestamp(value);
        return date ? date.getTime() : 0;
      })
    );

    return latestTimestamp > 0 ? new Date(latestTimestamp).toLocaleString() : "—";
  }, [barRecords]);

  const summary = useMemo(() => {
    if (!barRecords || barRecords.length === 0) {
      return { withPromotions: 0, withOwners: 0 };
    }

    return barRecords.reduce(
      (acc, record) => {
        if (record.has_active_promotion) {
          acc.withPromotions += 1;
        }
        if (record.owner_email) {
          acc.withOwners += 1;
        }
        return acc;
      },
      { withPromotions: 0, withOwners: 0 }
    );
  }, [barRecords]);

  const gridTemplateColumns = useMemo(() => {
    const widths = COLUMN_DEFINITIONS.map((column) => column.width || "200px");
    return ["50px", ...widths].join(" ");
  }, []);

  return (
    <StyledCard>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Supabase Staged Bars ({barRecords.length} records)</Typography>
          <Box display="flex" gap={1} alignItems="center">
            {selectedRows.size > 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<Delete />}
                onClick={onRequestDelete}
              >
                Delete {selectedRows.size} Record{selectedRows.size !== 1 ? "s" : ""}
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={onRefresh} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography>Loading staged bar records...</Typography>
          </Box>
        ) : barRecords.length > 0 ? (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                These records live in Supabase `temp_bars` and act as a staging area before promotion to the production `bars` table.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <strong>Latest updated at:</strong> {latestCreatedAt}
              </Typography>
            </Box>

            <Box mb={2} display="flex" gap={1} flexWrap="wrap">
              <Chip label={`Active promotions: ${summary.withPromotions}`} variant="outlined" color="primary" size="small" />
              <Chip label={`With owner email: ${summary.withOwners}`} variant="outlined" color="primary" size="small" />
            </Box>

            <Box
              sx={{
                overflowX: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                position: "relative"
              }}
            >
              <Box
                sx={{
                  maxHeight: "600px",
                  overflowY: "auto"
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridTemplateColumns,
                    gap: "1px",
                    backgroundColor: "#f5f5f5",
                    minWidth: "2200px"
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Checkbox
                      checked={selectedRows.size === barRecords.length && barRecords.length > 0}
                      indeterminate={selectedRows.size > 0 && selectedRows.size < barRecords.length}
                      onChange={onSelectAll}
                    />
                  </Box>
                  {COLUMN_DEFINITIONS.map((column) => (
                    <Box
                      key={column.label}
                      sx={{
                        p: 2,
                        backgroundColor: "white",
                        fontWeight: "bold",
                        borderBottom: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 10
                      }}
                    >
                      {column.label}
                    </Box>
                  ))}

                  {barRecords.map((record) => (
                    <React.Fragment key={record.bar_id}>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "white",
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Checkbox
                          checked={selectedRows.has(record.bar_id)}
                          onChange={() => onSelectRow(record.bar_id)}
                        />
                      </Box>
                      {COLUMN_DEFINITIONS.map((column) => (
                        <Box
                          key={`${record.bar_id}-${column.label}`}
                          sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}
                        >
                          {column.render(record)}
                        </Box>
                      ))}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No staged bar records available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Run the Firestore migration to repopulate Supabase `temp_bars` before review.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

const renderCategories = (categories) => {
  if (!categories) {
    return (
      <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic" }}>
        N/A
      </Typography>
    );
  }

  const filteredEntries = Object.entries(categories).filter(
    ([day]) => !["bar_id"].includes(day)
  );

  if (filteredEntries.length === 0) {
    return (
      <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic" }}>
        N/A
      </Typography>
    );
  }

  return (
    <Box>
      {filteredEntries.map(([day, value]) => {
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;
          if (!parsed || Object.keys(parsed).length === 0) {
            return null;
          }

          const summary = Object.entries(parsed)
            .map(([type, items]) => `${type}: ${(items || []).join(", ")}`)
            .join(" | ");

          if (!summary) {
            return null;
          }

          return (
            <Typography key={day} variant="caption" sx={{ display: "block", fontSize: "0.7rem", mb: 0.5 }}>
              <strong>{capitalize(day)}:</strong> {summary}
            </Typography>
          );
        } catch (error) {
          return null;
        }
      })}
    </Box>
  );
};

const formatTimestamp = (value) => {
  const date = parseTimestamp(value);
  return date ? date.toLocaleString() : "—";
};

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value.seconds) return new Date(value.seconds * 1000);
  if (value.toDate) {
    try {
      return value.toDate();
    } catch (error) {
      return null;
    }
  }
  return null;
};

const formatCrowdCount = (crowdCount) => {
  if (!crowdCount || typeof crowdCount !== "object") {
    return "—";
  }

  const total = crowdCount.total ?? "?";
  const singles = crowdCount.singles ?? "?";
  const lastUpdated = crowdCount.lastUpdated || crowdCount.last_updated;
  const lastUpdatedText = lastUpdated ? ` • Updated ${formatTimestamp(lastUpdated)}` : "";

  return `Total: ${total}, Singles: ${singles}${lastUpdatedText}`;
};

const formatHours = (hours) => {
  if (!hours || typeof hours !== "object") {
    return "—";
  }

  const openDays = Object.entries(hours)
    .filter(([, dayHours]) => dayHours && dayHours.isOpen)
    .map(([day, dayHours]) => {
      const openOne = dayHours.openTime || dayHours.open_time;
      const closeOne = dayHours.closeTime || dayHours.close_time;
      return `${capitalize(day)}: ${openOne || "?"} - ${closeOne || "?"}`;
    });

  return openDays.length > 0 ? openDays.join(" | ") : "—";
};

const formatLocation = (location) => {
  if (!location) {
    return "—";
  }

  if (typeof location === "string") {
    return location;
  }

  if (typeof location === "object") {
    if (location.type === "Point" && Array.isArray(location.coordinates)) {
      return `POINT(${location.coordinates.join(" ")})`;
    }

    if (location.longitude !== undefined && location.latitude !== undefined) {
      return `POINT(${location.longitude} ${location.latitude})`;
    }
  }

  return JSON.stringify(location);
};

const capitalize = (value) => {
  if (typeof value !== "string" || value.length === 0) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export default BarRecordsPreview;
